// Small fetch wrapper: same-origin cookies, the CSRF header on every call, JSON in and out.
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: { 'x-plana-csrf': '1', ...(body === undefined ? {} : { 'content-type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : {};
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? `Request failed (${res.status}).`);
  return data as T;
}

export interface UploadedAsset { id: string; mime: string; width: number; height: number; bytes: number }

export async function uploadImage(projectId: string, file: Blob, name = 'image'): Promise<UploadedAsset> {
  const form = new FormData();
  form.append('file', file, name);
  const res = await fetch(`/api/projects/${projectId}/assets`, { method: 'POST', credentials: 'same-origin', headers: { 'x-plana-csrf': '1' }, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Upload failed (${res.status}).`);
  return data.asset;
}

export const assetUrl = (id: string, w?: 320 | 640 | 1280) => `/api/assets/${id}${w ? `?w=${w}` : ''}`;

export interface MapConfig { tileUrl: string; attribution: string }
export interface PlaceResult { name: string; displayName: string; lat: number; lon: number; kind: string }
export interface AlongPlace { placeCardId: string; name: string; alongM: number; offRouteM: number }
export interface AlongResult { routeId: string; corridorKm: number; lengthM: number; approximate: boolean; places: AlongPlace[] }
export interface ImportedRoute { id: string; name: string; colour: string; lengthM: number; pointCount: number; parts: number; hasElevation: boolean; hasTime: boolean }
export interface GpxImport { assetId: string; routes: ImportedRoute[]; skipped: string[] }

/** Sends a GPX file to the server, which keeps the original and adds one route per track. */
export async function uploadGpx(projectId: string, file: File): Promise<GpxImport> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await fetch(`/api/projects/${projectId}/routes/gpx`, { method: 'POST', credentials: 'same-origin', headers: { 'x-plana-csrf': '1' }, body: form });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Import failed (${res.status}).`);
  return data as GpxImport;
}

export interface Me { id: string; email: string; name: string }
export interface Member { id: string; name: string; email: string; role: 'owner' | 'member' }
export interface Project { id: string; title: string; timeWindow: string; ownerId: string; updatedAt: string; role: 'owner' | 'member'; members: Member[] }
export interface Preview { url: string; title: string; description: string; siteName: string; imageAssetId: string | null; status: 'ok' | 'failed' }
export interface AgentToken { id: string; label: string; prefix: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }
